import { useEffect, useState } from 'react';
import { Container, Typography, Button, Box, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import RankingCompact from '../Rankings/components/RankingCompact';
import Forum from '../../components/Forum';

// --- Estilização do Card (Para a disposição Imagem-Esquerda, Texto-Direita) ---
const CustomCard = styled(Card)(({ theme }) => ({
	display: 'flex',
	minHeight: 220,
	boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
	border: '1px solid rgba(148, 163, 184, 0.35)',
	transition: 'transform 0.2s',
	cursor: 'pointer',
	'&:hover': {
		transform: 'translateY(-4px)',
		boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
	},
}));

const sectionSpacing = { xs: 4, sm: 6 };

// --- Componente Principal ---
const Home = () => {
	const navigate = useNavigate();
	const [preConfiguredScreeners, setPreConfiguredScreeners] = useState([]);

	useEffect(() => {
		const loadScreeners = async () => {
			try {
				const response = await fetch('/files/filters/preconfigured-screeners.json');
				if (!response.ok) {
					throw new Error('Erro ao carregar os screeners pré-configurados');
				}

				const data = await response.json();
				setPreConfiguredScreeners(data);
			} catch (error) {
				console.error('Não foi possível carregar os screeners pré-configurados', error);
			}
		};

		loadScreeners();
	}, []);

	return (
		<Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 5, sm: 8 } }}>

			{/* 1. CHAMADA PRINCIPAL (TOPO) */}
			<Box
				sx={{
					py: 6,
					textAlign: 'center',
					borderRadius: 3,
					mb: sectionSpacing,
					bgcolor: 'grey.900',
					color: '#fff',
					overflow: 'hidden',
					position: 'relative',
				}}
				>
				<Box
					component="picture"
					sx={{
						position: 'absolute',
						inset: 0,
						zIndex: 0,
						display: 'block',
					}}
				>
					<source srcSet="/images/common/stocks-charts-002.webp" type="image/webp" />
					<Box
						component="img"
						src="/images/common/stocks-charts-002.jpg"
						alt=""
						fetchpriority="high"
						decoding="async"
						aria-hidden
						sx={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							display: 'block',
						}}
					/>
				</Box>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						zIndex: 1,
						background: 'linear-gradient(120deg, rgba(8,12,20,0.8), rgba(13,40,70,0.6))',
					}}
				/>
				<Box sx={{ position: 'relative', zIndex: 2 }}>
					<SearchIcon sx={{ fontSize: 60, color: '#fff', mb: 1 }} />
					<Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#fff' }}>
						Encontre as Melhores Oportunidades
					</Typography>
					<Typography variant="h6" sx={{ m: 3, color: 'rgba(255,255,255,0.9)' }}>
						Crie seu próprio screener e filtre entre mais de 5000 ativos do Brasil e do Mundo com os critérios que realmente importam para sua estratégia de investimento, descobrindo oportunidades valiosas antes de todo mundo.
					</Typography>
					<Button
						variant="contained"
						size="large"
						startIcon={<SettingsIcon />}
						sx={{ py: 1.5, px: 4, fontSize: '1.1rem' }}
						onClick={() => navigate('/stockscreener/0')}
					>
						Começar a Filtrar
					</Button>
				</Box>
			</Box>

			{/* 2. TEXTO DE INTRODUÇÃO À LISTA */}
			<Box sx={{ textAlign: 'center', mb: sectionSpacing }}>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Não sabe por onde começar? Use os nossos screeners pré-configurados!
				</Typography>
			</Box>

			{/* LISTA COM MOLDURA EXTERNA */}
			<Box
				sx={{
					mb: sectionSpacing,
					border: '1px solid rgba(148, 163, 184, 0.4)',
					borderRadius: 4,
					boxShadow: '0 20px 60px rgba(15,23,42,0.08)',
					backgroundColor: '#fff',
					px: { xs: 2, sm: 4 },
					py: { xs: 3, sm: 4 },
				}}
				>
				{/* 3. LISTA DE CARDS (Layout Grid Responsivo) */}
				<Grid container spacing={4}>
					{preConfiguredScreeners.map((screener) => {
						const webpImage = screener.image?.replace(/\.(png|jpe?g)$/i, '.webp');
						return (
						<Grid item xs={12} sm={6} md={6} key={screener.id || screener.title} sx={{ display: 'flex' }}>
							<CustomCard onClick={() => navigate(`/stockscreener/${screener.id}`)} sx={{ width: '100%' }}>

								{/* Imagem/Ícone na Esquerda */}
								<CardMedia
									component="picture"
									sx={{
										width: 150,
										height: '100%',
										objectFit: 'cover',
										display: { xs: 'none', sm: 'block' },
										flexShrink: 0,
									}}
								>
									{webpImage && <source srcSet={webpImage} type="image/webp" />}
									<Box
										component="img"
										src={screener.image}
										alt={screener.title}
										loading="lazy"
										sx={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
											display: 'block',
										}}
									/>
								</CardMedia>

								{/* Título e Descrição na Direita */}
								<CardContent sx={{ flex: 1, pr: 3, display: 'flex', flexDirection: 'column' }}>
									<Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>{screener.title}</Typography>
									<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>{screener.caption}</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											flexGrow: 1,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											display: '-webkit-box',
											WebkitLineClamp: 7,
											WebkitBoxOrient: 'vertical',
										}}
										>
										{screener.description}
									</Typography>
								</CardContent>

							</CustomCard>
						</Grid>
					)})}
				</Grid>

				<Box sx={{ textAlign: 'center', mt: 3 }}>
					<Button variant="contained" size="large" onClick={() => navigate('/stockscreener')}>
						Veja todos
					</Button>
				</Box>
			</Box>

				{/* CTA para o Gerenciador de Portfólio */}
				<Box
					sx={{
						mt: sectionSpacing,
						py: { xs: 4, sm: 6 },
						px: { xs: 2, sm: 6 },
						textAlign: 'center',
						borderRadius: 3,
						bgcolor: 'grey.900',
						color: '#fff',
						overflow: 'hidden',
						position: 'relative',
					}}
					>
					<Box
						component="picture"
						sx={{
							position: 'absolute',
							inset: 0,
							zIndex: 0,
							display: 'block',
						}}
					>
						<source srcSet="/images/common/stocks-charts-001.webp" type="image/webp" />
						<Box
							component="img"
							src="/images/common/stocks-charts-001.jpg"
							alt=""
							decoding="async"
							aria-hidden
							sx={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								display: 'block',
							}}
						/>
					</Box>
					<Box
						sx={{
							position: 'absolute',
							inset: 0,
							zIndex: 1,
							background: 'linear-gradient(120deg, rgba(10,16,28,0.92), rgba(0,68,120,0.7))',
						}}
					/>
					<Box sx={{ position: 'relative', zIndex: 2 }}>
						<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Já escolheu seus ativos?</Typography>
						<Typography variant="h6" sx={{ mb: { xs: 3, sm: 4 }, color: 'rgba(255,255,255,0.9)' }}>
							Agora faça a analise usando nossas ferramentas e mantenha tudo sob controle com o nosso Gerenciador de Portfólio.
						</Typography>
						<Button
							variant="contained"
							size="large"
							color="secondary"
							sx={{ fontSize: '1rem', px: 4 }}
							onClick={() => navigate('/portfolio/ACAO')}
						>
							Abrir Gerenciador de Portfólio
						</Button>
					</Box>
				</Box>

				{/* 4. TEXTO DE INTRODUÇÃO AO RANKING */}
				<Box sx={{ textAlign: 'center', mt: sectionSpacing, mb: sectionSpacing }}>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						Ainda indeciso? Navegue pelas nossas listas.
					</Typography>
				</Box>

				<Box sx={{
					mb: sectionSpacing,
					px: { xs: 2, sm: 4 },
					py: { xs: 3, sm: 5 },
					borderRadius: 4,
					background: '#fff',
					boxShadow: '0 20px 60px rgba(15,23,42,0.12)',
					border: '1px solid rgba(148, 163, 184, 0.3)',
					}}>
					<RankingCompact />
				</Box>

				{/* 4. TEXTO DE INTRODUÇÃO AO RANKING */}
				<Box sx={{ textAlign: 'center', mt: sectionSpacing, mb: sectionSpacing }}>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Tem uma tese foda? Compartilhe com nossa comunidade!
				</Typography>
			</Box>		

			<Box
				sx={{
					mt: sectionSpacing,
					borderRadius: 4,
					background: '#fff',
					border: '1px solid rgba(148, 163, 184, 0.4)',
					boxShadow: '0 20px 60px rgba(15,23,42,0.08)',
					p: { xs: 2, sm: 3 },
				}}
				>
				<Forum/>	
			</Box>
				
		</Container>
	);
};

export default Home;
